/**
 * Press releases (newsroom) content collection.
 *
 * This is the single source of truth for press release content. It is consumed
 * by BOTH the web app (the /press pages, RSS feed and prerender list) AND the
 * API (which renders an on-demand PDF media version of each release), so the
 * downloadable PDF can never drift from the published web page.
 *
 * House style: UK English, no em dashes anywhere in copy.
 *
 * Inline markup supported inside block `text` / list `items`:
 *   - **bold**
 *   - [label](/internal-path) or [label](https://external)
 *
 * When adding a release, also add its <loc> to apps/web/public/sitemap.xml.
 * The generated Render API manifest then picks up its PUBLIC_ROUTES entry.
 */
export const SITE_URL = 'https://fixtyer.co.uk';
export const PRESS_CONTACT_EMAIL = 'press@fixtyer.co.uk';
/** Boilerplate used in every release's "Notes to editors" / "About Fixtyer" block. */
export const ABOUT_FIXTYER_BOILERPLATE = 'Fixtyer is a self-service platform that helps UK drivers prepare and submit their own pothole damage claims, without solicitors and without commission, so drivers keep 100% of any compensation. Fixtyer also analyses published Department for Transport and council data and publishes a road maintenance rating for every highway authority in England.';
/** Standard attribution line for DfT / council data (matches /media-enquiries). */
export const DATA_ATTRIBUTION_LINE = 'Fixtyer analysis of Department for Transport and council data';
/** Open Government Licence note for public-sector source data. */
export const OGL_NOTE = 'Underlying public-sector figures (pothole counts, road-condition percentages, maintenance spend and DfT scorecard ratings) are sourced from published Department for Transport releases and councils\u2019 own transparency reports, used under the Open Government Licence v3.0.';
/** Not-legal-advice disclaimer for every release. */
export const NOT_LEGAL_ADVICE_LINE = 'Fixtyer provides documents, guidance and information only. It is not a law firm, does not provide legal advice, does not act on behalf of drivers, and does not guarantee compensation.';
// Add new releases here. Newest-first ordering is handled at render time.
export const pressReleases = [
    {
        title: 'Fixtyer Road Maintenance Index counts 16 GREEN, 125 AMBER and 13 RED English authorities',
        slug: 'road-maintenance-index-q3-2026',
        date: '2026-07-19',
        displayDate: 'Q3 2026',
        standfirst: 'The first Fixtyer Road Maintenance Index presents the Department for Transport overall road maintenance bands for all 154 rated English highway authorities, with national and regional tables and no composite score.',
        pdfSubheading: 'The Q3 2026 edition is tied to the versioned 2025-2026-v1 dataset and provides reproducible national and regional band counts.',
        locationDateline: 'England',
        dataSources: 'The Index is a deterministic presentation of the Department for Transport 2025-2026 network-level rating bands, using Fixtyer dataset version 2025-2026-v1.',
        sources: [
            {
                label: 'Fixtyer Road Maintenance Index Q3 2026',
                url: 'https://fixtyer.co.uk/road-maintenance-index',
            },
            {
                label: 'Fixtyer canonical council ratings dataset and methodology',
                url: 'https://fixtyer.co.uk/data/council-road-maintenance-ratings',
            },
            {
                label: 'Department for Transport local road maintenance ratings 2025 to 2026',
                url: 'https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026',
            },
        ],
        interviewsNote: 'The methodology note, regional and authority briefs, and rapid factual checks are available through Fixtyer media enquiries.',
        mediaContactName: 'Jayne Coleman',
        mediaContactEmail: 'jayne@fixtyer.co.uk',
        pdfFilename: 'fixtyer-road-maintenance-index-q3-2026',
        body: [
            {
                type: 'paragraph',
                text: 'Fixtyer has published the first [Fixtyer Road Maintenance Index](/road-maintenance-index), a transparent national and regional presentation of the Department for Transport road maintenance rating bands for England.',
            },
            {
                type: 'paragraph',
                text: 'Across all 154 rated English highway authorities, the DfT overall bands comprise **16 GREEN, 125 AMBER and 13 RED**. The Index reports those deterministic counts and does not create a composite score, ordinal ranking, pothole total or repair backlog.',
            },
            {
                type: 'heading',
                text: 'What the Q3 2026 edition covers',
            },
            {
                type: 'paragraph',
                text: 'The publication includes a national table, one release and accessible table for every distinct region in the canonical manifest, and a data-led journalist brief for every rated authority. All assets use the same [2025-2026-v1 dataset](/data/council-road-maintenance-ratings).',
            },
            {
                type: 'paragraph',
                text: 'Q3 2026 identifies the Fixtyer publication edition. It does not imply that DfT updates the ratings quarterly. A later Fixtyer edition may continue to use the latest official dataset until the Department for Transport publishes a replacement.',
            },
            {
                type: 'heading',
                text: 'Method and limitations',
            },
            {
                type: 'paragraph',
                text: 'Fixtyer groups authorities by the region recorded in its canonical manifest and counts each published overall GREEN, AMBER or RED band. Ties and false precision are avoided because no score or rank is calculated.',
            },
            {
                type: 'paragraph',
                text: 'The ratings are network-level bands. They do not establish notice, negligence, liability, the condition of a particular road or the merits of an individual claim.',
            },
            {
                type: 'paragraph',
                text: 'Journalists can request the methodology note or a rapid factual check through [Fixtyer media enquiries](/media-enquiries). Open correction and update information is published with the canonical dataset.',
            },
        ],
    },
    {
        title: '"I see it every week": mechanic warns summer road trips could expose hidden pothole damage',
        slug: 'mechanic-warns-summer-pothole-damage',
        date: '2026-06-15',
        displayDate: 'Summer 2026',
        standfirst: 'An independent West Midlands mechanic is warning drivers to check for hidden pothole damage before long summer journeys, as heat, load and motorway speeds can expose tyre, wheel, steering and suspension faults dismissed months earlier.',
        pdfSubheading: 'Fixtyer urges drivers to check tyres, wheels and suspension before holiday journeys, and to keep evidence if pothole damage is found.',
        locationDateline: 'United Kingdom',
        hasNamedExpert: true,
        expertName: 'Elliot Wheeler',
        expertCredential: 'City & Guilds Level 3 qualified mechanic',
        expertJobTitle: 'Qualified Mechanic',
        expertWorksFor: 'Your Local Mechanic',
        expertBio: 'Elliot Wheeler is the founder and lead mechanic of Your Local Mechanic, an independent mobile mechanic service covering Dudley, Stourbridge, Halesowen, Brierley Hill, Kingswinford and the wider Black Country. He is City & Guilds Level 3 qualified and has more than 15 years of experience. Your Local Mechanic has more than 370 customer reviews across Google, Trustpilot and other review platforms, according to its website.',
        dataSources: 'National statistics cited are drawn from published RAC pothole data.',
        sources: [
            {
                label: 'RAC Pothole Index (pothole breakdown statistics)',
                url: 'https://www.rac.co.uk/drive/advice/driving-advice/rac-pothole-index-statistics-data-and-projections/',
            },
        ],
        interviewsNote: 'Interviews with Elliot Wheeler and a Fixtyer spokesperson are available on request. High-resolution images are available.',
        mediaContactName: 'Jayne Coleman',
        mediaContactEmail: 'jayne@fixtyer.co.uk',
        pdfFilename: 'fixtyer-press-release-summer-road-trips-hidden-pothole-damage',
        body: [
            {
                type: 'paragraph',
                text: 'As families load up their cars for summer trips, an independent West Midlands mechanic is warning drivers to check for hidden pothole damage before setting off on long journeys.',
            },
            {
                type: 'paragraph',
                text: 'Elliot Wheeler, a City & Guilds Level 3 qualified mechanic with more than 15 years of experience, says summer driving can expose damage that drivers may have dismissed months earlier, particularly to tyres, wheels, steering and suspension.',
            },
            {
                type: 'quote',
                text: 'The damage that costs people the most is almost never the damage they can see straight away. They hit a pothole in winter or early spring, the car still drives, so they assume it is fine and forget about it. Then they load the car up, get it hot on a motorway for three hours, and that is exactly when a weakened tyre or tired suspension component can become a much bigger problem. I see it every week.',
                cite: 'Elliot Wheeler, Your Local Mechanic',
            },
            {
                type: 'paragraph',
                text: 'Wheeler says the most [commonly missed damage](/blog/mechanics-view-hidden-pothole-damage) includes tyre sidewall bulges, tracking and alignment problems, cracked or buckled wheels, broken suspension springs and worn or damaged shock absorbers.',
            },
            {
                type: 'quote',
                text: 'People often check the tread and think the tyre looks fine, but pothole damage can show up on the sidewall. You are looking for a bulge, almost like a blister in the rubber. That can mean the tyre structure has been damaged. It is a serious safety issue, and it is exactly the kind of thing drivers do not want to discover at 70mph with a fully loaded car.',
                cite: 'Elliot Wheeler',
            },
            {
                type: 'paragraph',
                text: 'He is urging drivers to carry out a simple pre-trip check before any long journey:',
            },
            {
                type: 'list',
                items: [
                    'look carefully at both the inner and outer tyre sidewalls, not just the tread',
                    "set tyre pressures for a fully loaded vehicle using the manufacturer's guidance",
                    'listen for knocking over bumps',
                    'watch for pulling to one side, steering vibration or uneven tyre wear',
                    'get the vehicle checked if it has taken a hard pothole impact, even if it still feels driveable',
                ],
            },
            {
                type: 'paragraph',
                text: 'The warning comes after a sharp rise in pothole-related breakdown reports at the start of 2026. According to the RAC, 11,396 drivers mentioned potholes when logging breakdowns in January and February 2026, an average of 193 a day. In February alone, 6,290 drivers mentioned potholes, compared with 1,842 in the same month in 2025.',
            },
            {
                type: 'paragraph',
                text: 'In 2025, RAC patrols attended 26,048 breakdowns involving damaged shock absorbers, broken suspension springs or distorted wheels, faults the motoring organisation says are most likely to be caused by wear and tear from poor road surfaces. The RAC estimates the cost of pothole repairs to a typical family car can be up to £590 for damage worse than a puncture.',
            },
            {
                type: 'paragraph',
                text: 'Wheeler says the safety risk is not limited to the damage itself.',
            },
            {
                type: 'quote',
                text: 'What really worries me is what people do to avoid potholes. Drivers tell me they had to swerve into the other side of the road to miss one. A hole in the road should not be forcing someone towards oncoming traffic, but that is what is happening. One day it will not just be a bent wheel.',
                cite: 'Elliot Wheeler',
            },
            {
                type: 'paragraph',
                text: 'Self-service pothole claims platform **Fixtyer**, which is working with Wheeler to help drivers understand the practical signs of pothole damage, says motorists should also think about evidence before they repair the vehicle.',
            },
            {
                type: 'quote',
                text: 'If a summer check reveals damage from a pothole you remember hitting, do not just repair it and throw the paperwork away. Photograph the damage, record where the impact happened, keep the invoice or quote, and check whether the pothole had already been reported. Those details can make a major difference if you decide to submit a claim.',
                cite: 'Fixtyer spokesperson',
            },
            {
                type: 'paragraph',
                text: 'Fixtyer helps drivers prepare their own pothole damage claim packs, including evidence checklists, professional claim documents, [authority-specific guidance](/councils) and [checks for previous public reports](/guides/prior-reports-explained) of the same defect. Drivers submit the claim themselves and keep 100% of any compensation recovered.',
            },
            {
                type: 'quote',
                text: 'Elliot sees the damage. We help drivers organise the evidence around the cost of that damage. A good claim is not built on frustration alone. It is built on location, photographs, repair evidence and a clear link between the pothole and the damage.',
                cite: 'Fixtyer spokesperson',
            },
            {
                type: 'paragraph',
                text: "Wheeler's message to drivers before they set off this summer is simple.",
            },
            {
                type: 'quote',
                text: 'If you took a hard knock from a pothole earlier in the year, get the car checked before a long trip. Two minutes looking properly at the tyres, wheels and suspension could save you from a roadside emergency, and if the damage was caused by a road that should have been maintained, it may also help you recover the repair cost.',
                cite: 'Elliot Wheeler',
            },
        ],
    },
    {
        title: 'Pothole-related breakdown reports more than triple as drivers warned not to write off repair bills they may be able to challenge',
        slug: 'pothole-breakdown-reports-triple',
        date: '2026-03-20',
        displayDate: 'Spring 2026',
        standfirst: 'Pothole-related breakdown reports more than tripled in early 2026 as repair bills climbed. Fixtyer is urging drivers not to write off pothole damage costs that may be recoverable from the authority responsible for the road.',
        pdfSubheading: 'Fixtyer urges drivers not to write off pothole repair bills that may be recoverable from the authority responsible for the road.',
        locationDateline: 'United Kingdom',
        hasNamedExpert: true,
        expertName: 'Elliot Wheeler',
        expertCredential: 'Qualified mechanic',
        expertJobTitle: 'Qualified Mechanic',
        expertWorksFor: 'Your Local Mechanic',
        expertBio: 'Elliot Wheeler is the founder of Your Local Mechanic, an independent mobile mechanic service in the West Midlands, with more than 15 years of experience.',
        dataSources: "National statistics cited are drawn from published RAC pothole data, the Department for Transport's local road maintenance ratings and the Asphalt Industry Alliance ALARM 2026 survey.",
        sources: [
            {
                label: 'RAC Pothole Index (pothole breakdown statistics)',
                url: 'https://www.rac.co.uk/drive/advice/driving-advice/rac-pothole-index-statistics-data-and-projections/',
            },
            {
                label: 'Department for Transport local road maintenance ratings 2025 to 2026',
                url: 'https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026',
            },
            {
                label: 'Asphalt Industry Alliance ALARM survey 2026',
                url: 'https://www.asphaltuk.org/alarm-survey-page/',
            },
        ],
        interviewsNote: 'Interviews with a Fixtyer spokesperson and Elliot Wheeler are available on request.',
        mediaContactName: 'Jayne Coleman',
        mediaContactEmail: 'jayne@fixtyer.co.uk',
        pdfFilename: 'fixtyer-press-release-pothole-breakdowns-early-2026',
        body: [
            {
                type: 'paragraph',
                text: 'Pothole-related breakdown reports surged in the opening months of 2026, as drivers faced rising repair bills from damaged tyres, wheels, suspension and steering components.',
            },
            {
                type: 'paragraph',
                text: 'Self-service pothole claims platform **Fixtyer** is urging drivers not to treat pothole damage as an unavoidable private cost, and to check whether they have the evidence needed to claim from the authority responsible for maintaining the road.',
            },
            {
                type: 'paragraph',
                text: 'According to the RAC, 11,396 drivers mentioned potholes when logging breakdowns in January and February 2026, an average of 193 a day. February alone saw 6,290 reports, compared with 1,842 in the same month in 2025. Reports peaked at 336 drivers on Friday 6 February, more than 2.5 times the highest daily figure recorded during the whole of 2025.',
            },
            {
                type: 'paragraph',
                text: 'The financial impact can be significant. The RAC estimates that pothole damage worse than a puncture can cost up to £590 to repair, with common issues including damaged suspension springs, shock absorbers and distorted wheels.',
            },
            {
                type: 'paragraph',
                text: "The wider road maintenance picture remains severe. The Asphalt Industry Alliance's ALARM 2026 survey reports a record £18.62 billion backlog of carriageway repairs across England and Wales, which would take 12 years to clear. Roads are now resurfaced, on average, only once every 97 years.",
            },
            {
                type: 'paragraph',
                text: "The Department for Transport's 2025/26 [local road maintenance ratings](/councils) also show that only 16 local highway authorities in England are rated green, compared with 125 rated amber and 13 rated red.",
            },
            {
                type: 'paragraph',
                text: 'Fixtyer says the figures should prompt drivers to think differently about pothole damage.',
            },
            {
                type: 'quote',
                text: 'Most people hit a pothole, pay the garage bill, complain about the state of the roads, and move on. But if the damage happened on a road that should have been properly maintained, that cost may be recoverable. The key question is not just whether there was a pothole. It is whether the driver can show where it was, what damage it caused, what the repair cost, and whether the authority had or should have had enough information to deal with the defect.',
                cite: 'Fixtyer spokesperson',
            },
            {
                type: 'paragraph',
                text: 'Fixtyer helps drivers prepare their own pothole damage claim packs, including evidence checklists, professional claim documents, authority-specific guidance and [checks for previous public reports](/guides/prior-reports-explained) of the same defect.',
            },
            {
                type: 'paragraph',
                text: 'The company says prior reports can be especially important because councils and highway authorities may reject claims by arguing they had a [reasonable inspection and repair system](/guides/section-58-defence) in place, or that they were not aware of the defect before the incident.',
            },
            {
                type: 'quote',
                text: 'Pothole claims are evidence-led. A driver with a clear location, good photographs, repair evidence and a prior report of the same pothole is in a much stronger position than someone relying on a short description after the event. Fixtyer exists to help ordinary drivers organise that evidence properly and submit their own claim without giving away a percentage of any compensation.',
                cite: 'Fixtyer spokesperson',
            },
            {
                type: 'paragraph',
                text: 'Elliot Wheeler, an independent qualified mechanic with more than 15 years of experience, says the national figures match what he sees in day-to-day repair work.',
            },
            {
                type: 'quote',
                text: "It's never been this bad in all the time I've been doing this. Pothole jobs have easily doubled in the last few years. And the part that frustrates me is how many people have no idea they could even ask for that money back. They just pay up. It's their money, and most of them never even check.",
                cite: 'Elliot Wheeler, Your Local Mechanic',
            },
            {
                type: 'paragraph',
                text: "Fixtyer's advice to any driver who has hit a pothole is straightforward: stop only where it is safe, photograph the pothole and vehicle damage, record the exact location, get the damage professionally assessed, keep all receipts, and check whether the defect had already been reported to the council or highway authority.",
            },
        ],
    },
];
/** Releases sorted newest first (by ISO date). */
export const pressReleasesByDateDesc = [...pressReleases].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
export function getPressRelease(slug) {
    return pressReleases.find((release) => release.slug === slug);
}
export const pressFeed = pressReleasesByDateDesc.map((release) => ({
    title: release.title,
    slug: release.slug,
    date: release.date,
    standfirst: release.standfirst,
}));
